/* eslint-disable deprecation/deprecation */
import * as React from 'react';
import { DisplayValueFormatterProps, IconLibraryIcon } from './ListMenuDisplayFormatters/DisplayValueFormatter';
import { TextMenuDisplayFormatter } from './ListMenuDisplayFormatters/TextMenuDisplayFormatter';

export { DisplayValueFormatterProps, IconLibraryIcon };

export interface MenuItemInfoBase {
  /**
   * One of:
   * * a (usually `'data:image'`) url string for a 14x14px icon or
   * * an icon from the @tableau/icons library, or
   * * plain text for e.g. shortcut text.
   *
   * Will be shown on the right side of the item. Ignored for items with a submenu.
   */
  annotation?: string | IconLibraryIcon;
  /** `true` for a checkmark, `false` if checkable but not checked, `undefined` otherwise */
  checked?: boolean;
  /**
   * Used (sparingly) to show explanatory text for a menu item. When provided, an `icon` must also be provided.
   */
  detail?: string;
  /** If the option is disabled */
  disabled?: boolean;
  /**
   * A (usually `'data:image'`) url string for a 14x14px icon, or an icon from the @tableau/icons library.
   * Will be shown on the left side of the item. Ignored for checked items and group label items.
   */
  icon?: string | IconLibraryIcon;
  /** If the option is a divider and not a selectable option */
  isDivider?: boolean;
  /** If the option is the label for a menu item group. The group continues until the next group item or divider item. */
  isGroup?: boolean;
  /** Max height of the menu item's sub menu in px */
  subMenuMaxHeight?: number;
  /** Style overrides for the menu item's sub menu - can be used for setting the width of the menu */
  subMenuStyle?: React.CSSProperties;
  /** The value of the item - this is not the displayed string */
  value?: string;
}

/**
 * This is the original MenuItemInfo which only rendered text in the dropdown. It is used to create
 * a GenericMenuItemInfo with a Text formatter in the DropdownWidget or FlyoutListMenuWidget
 */
export interface MenuItemInfo extends MenuItemInfoBase {
  /** The displayed string of the item */
  displayValue?: string;
  /** The array of menu items in the item's submenu */
  menuItemInfos?: MenuItemInfo[];
  /**
   * A custom title for the item.
   * NOTE: The custom title will override the default title when the displayed value within the menu item is truncated.
   */
  title?: string;
}

/** @deprecated unsupported API, instead use `FlyoutListMenuWidget` */
export interface GenericMenuItemInfo extends MenuItemInfoBase {
  /**
   * This is a react component type (a class or function) that takes as props some data given by the dropdown
   * and rendered in the flyout list menu. This should typically have some data bound to it via closure.
   */
  displayFormatterBoundWithData: React.ComponentType<DisplayValueFormatterProps>;
  /**
   * If the item is unselectable. If set to true, this item will not be able to highlighted, navigated to, or selected. Display formatters
   * that may not be selectable could be Headers for a group of menu items.
   * @deprecated use `isGroup` for groups of menu items, to ensure the menu is exposed properly to Assistive Technology
   */
  isUnselectable?: boolean;
  /**
   * If the menu item is accessible via keyboard value (if it has text), provide the text here.
   * For example if the data bound with the display formatter is a text + icon, provide the text here
   */
  keyboardAccessibleValue?: string;
  /**  The array of menu items in the item's submenu */
  menuItemInfos?: GenericMenuItemInfo[];
  /**
   * If used in a dropdown, this is the text will appear in the dropdown button
   */
  textForDropdownButton?: string;
}

/**
 * This converts the original dropdowns with only text values to use the displayFormatter
 */
export const convertMenuItemInfoToGeneric = (basicMenuItemInfo: MenuItemInfo[], selectedValue?: string): GenericMenuItemInfo[] =>
  basicMenuItemInfo.map<GenericMenuItemInfo>(basic => ({
    annotation: basic.annotation,
    checked: (!!selectedValue && basic.value === selectedValue) || basic.checked,
    detail: basic.detail,
    disabled: basic.disabled,
    displayFormatterBoundWithData: TextMenuDisplayFormatter(basic.displayValue, basic.title),
    icon: basic.icon,
    isDivider: basic.isDivider,
    isGroup: basic.isGroup,
    isUnselectable: false,
    keyboardAccessibleValue: basic.displayValue,
    menuItemInfos: basic.menuItemInfos ? convertMenuItemInfoToGeneric(basic.menuItemInfos) : undefined,
    subMenuMaxHeight: basic.subMenuMaxHeight,
    subMenuStyle: basic.subMenuStyle,
    textForDropdownButton: basic.displayValue,
    value: basic.value,
  }));
